/**
 * La carta dell'Italia con le aziende (27/10/2026). Due strati SVG uno sopra l'altro:
 *  - la TERRA, nelle coordinate del mondo, che si ingrandisce cambiando il `viewBox` (i confini restano fini a ogni
 *    ingrandimento: `vector-effect: non-scaling-stroke`);
 *  - i SEGNI e i nomi, in pixel dello schermo, ricalcolati a ogni movimento: restano della stessa grandezza, e quelli
 *    che si toccherebbero diventano un gruppo (`segni.ts`).
 *
 * Si muove con la rotella o due dita sul tappetino, trascinando, pizzicando col dito, col doppio clic, coi bottoni e
 * da tastiera (+, −, frecce, 0 per tutta l'Italia, Esc per togliere la scelta). Toccare un gruppo lo apre avvicinandosi;
 * se le aziende sono nello stesso punto (stesso indirizzo), il gruppo le elenca nel pannello.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Paper, Tooltip, alpha } from '@mui/material';
import { Add, Remove, ZoomOutMap } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ContiPerLivello, LivelloMappa } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { COLORI_MAPPA } from './aspetto';
import {
  INGRANDIMENTO_MASSIMO,
  ingrandisciAttorno,
  inquadra,
  interpola,
  limita,
  morbido,
  sposta,
  versoSchermo,
  viewBox,
  type Camera,
  type Dimensioni,
  type Margini,
} from '@shared/mappa/camera';
import { caricaRegioni } from './confini';
import { RIQUADRO_ITALIA, proietta, regioneDelPunto, reticolo, type Regione } from '@shared/mappa/geometria';
import { aVentaglio, arcoFra, chiaveDelGruppo, comuniDelGruppo, nomeBreve, posaEtichette, raggioAlone, raggruppa, sommaConti, type Gruppo } from './segni';
import { CARATTERE_NOMI, NomeSullaMappa, RAGGIO_MINIMO_CON_LOGO, RAGGIO_SEDE, SegnoAzienda, SegnoGruppo, SegnoSede } from './SegniSullaMappa';

/**
 * Un segno sulla carta: UNA SEDE di un'azienda. Un'azienda con più sedi operative ha più segni, che rimandano tutti a
 * lei (`azienda`): la sede principale porta il segno grande (alone, nucleo, nome), le altre un segno piccolo dello
 * stesso colore. Scegliere un segno sceglie l'azienda, e si accendono tutte le sue sedi.
 */
export interface SegnoDaDisegnare {
  /** `azienda|sede`: unico sulla carta. */
  chiave: string;
  azienda: string;
  principale: boolean;
  nome: string;
  nome_sede: string | null;
  citta: string | null;
  lng: number;
  lat: number;
  livello: LivelloMappa;
  account: ContiPerLivello;
  account_totale: number;
  persone_attive: number;
  persone_adesso: number;
  colore: string | null;
  logo: string | null;
  spenta: boolean;
}

interface Props {
  segni: readonly SegnoDaDisegnare[];
  /** Chi ha portato chi su YumaOS (chiavi delle aziende): un arco dalla sede principale di `da` a quella di `verso`. */
  legami?: ReadonlyArray<{ da: string; verso: string }>;
  /** L'azienda scelta (non la sede). */
  selezionata: string | null;
  onSeleziona: (azienda: string | null) => void;
  /** Un gruppo di aziende nello stesso punto: non si separano avvicinandosi, si elencano. */
  onGruppo: (aziende: string[]) => void;
  /** Porta la carta su una sede (dalla scheda): cambia `n` per ripetere la stessa. */
  vaiAllaSede?: { chiave: string; n: number } | null;
  margini: Margini;
  /** Lo spazio da lasciare sotto i bottoni dello zoom (il foglio del telefono). */
  spazioControlliBasso: number;
  onConfiniNonDisponibili?: () => void;
}

const DURATA_VOLO_MS = 520;
const SOGLIA_TRASCINAMENTO = 5;
/**
 * Da quanto vicino (rispetto alla vista di tutta l'Italia) un gruppo nello stesso punto si apre a ventaglio: da qui in
 * giù nessun ingrandimento lo staccherebbe più, e a scala regionale i nomi entrano.
 */
const SOGLIA_VENTAGLIO = 5;
const PERCORSO_RETICOLO = reticolo();

const movimentoRidotto = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

let misuratore: CanvasRenderingContext2D | null = null;
function larghezzaTesto(testo: string): number {
  if (!misuratore && typeof document !== 'undefined') misuratore = document.createElement('canvas').getContext('2d');
  if (!misuratore) return testo.length * 7;
  misuratore.font = CARATTERE_NOMI;
  return misuratore.measureText(testo).width;
}

// ───────────────────────────────────────────────────────────── la terra

const Terra = React.memo<{ regioni: Regione[]; evidenziate: ReadonlySet<string>; colore: string; ombra: number }>(
  ({ regioni, evidenziate, colore, ombra }) => {
    const tutte = useMemo(() => regioni.map((r) => r.percorso).join(''), [regioni]);
    return (
      <>
        <path d={tutte} fill={COLORI_MAPPA.ombra} transform={`translate(0 ${ombra})`} />
        {/* La costa: un filo più scuro sotto le regioni, di cui resta visibile solo la metà esterna. */}
        <path d={tutte} fill={COLORI_MAPPA.terra} stroke="#C9C7BE" strokeWidth={1.6} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        {regioni.map((r) => (
          <path
            key={r.chiave}
            d={r.percorso}
            fill={evidenziate.has(r.chiave) ? alpha(colore, 0.16) : COLORI_MAPPA.terra}
            stroke={COLORI_MAPPA.confine}
            strokeWidth={0.8}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            style={{ transition: 'fill 320ms ease' }}
          />
        ))}
      </>
    );
  },
);
Terra.displayName = 'Terra';

// ───────────────────────────────────────────────────────────── la carta

export const MappaItalia: React.FC<Props> = ({
  segni, legami = [], selezionata, onSeleziona, onGruppo, vaiAllaSede, margini, spazioControlliBasso, onConfiniNonDisponibili,
}) => {
  const { t } = useTranslation('mappa');
  const contenitore = useRef<HTMLDivElement>(null);
  const [dim, setDim] = useState<Dimensioni | null>(null);
  const [regioni, setRegioni] = useState<Regione[] | null>(null);
  const [camera, setCamera] = useState<Camera | null>(null);
  const cameraRef = useRef<Camera | null>(null);
  cameraRef.current = camera;
  const mossaDallUtente = useRef(false);
  const volo = useRef<number | null>(null);
  const [entrata, setEntrata] = useState(true);

  // Le dimensioni del contenitore, sempre aggiornate (finestra, pannello, rotazione del telefono).
  useLayoutEffect(() => {
    const el = contenitore.current;
    if (!el) return;
    const misura = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setDim({ larghezza: r.width, altezza: r.height });
    };
    misura();
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(el);
    return () => osservatore.disconnect();
  }, []);

  useEffect(() => {
    let vivo = true;
    void caricaRegioni().then((r) => {
      if (!vivo) return;
      setRegioni(r);
      if (!r) onConfiniNonDisponibili?.();
    });
    return () => { vivo = false; };
  }, [onConfiniNonDisponibili]);

  useEffect(() => {
    const tempo = window.setTimeout(() => setEntrata(false), 1600);
    return () => window.clearTimeout(tempo);
  }, []);

  const { alto, destra, basso, sinistra } = margini;
  const base = useMemo(
    () => (dim ? inquadra(RIQUADRO_ITALIA, dim, { alto, destra, basso, sinistra }) : null),
    [dim, alto, destra, basso, sinistra],
  );

  // Finché l'utente non ha mosso la mappa, segue l'inquadratura di tutta l'Italia (anche se la finestra cambia).
  useEffect(() => {
    if (base && (!cameraRef.current || !mossaDallUtente.current)) setCamera(base);
  }, [base]);

  const fermaVolo = () => {
    if (volo.current !== null) cancelAnimationFrame(volo.current);
    volo.current = null;
  };

  const vaiA = useCallback((destinazione: Camera) => {
    if (!base) return;
    const arrivo = limita(destinazione, base.scala, RIQUADRO_ITALIA);
    const partenza = cameraRef.current;
    fermaVolo();
    if (!partenza || movimentoRidotto()) {
      setCamera(arrivo);
      return;
    }
    const inizio = performance.now();
    const passo = (ora: number) => {
      const t = Math.min(1, (ora - inizio) / DURATA_VOLO_MS);
      setCamera(interpola(partenza, arrivo, morbido(t)));
      volo.current = t < 1 ? requestAnimationFrame(passo) : null;
    };
    volo.current = requestAnimationFrame(passo);
  }, [base]);

  useEffect(() => () => fermaVolo(), []);

  const imposta = useCallback((c: Camera) => {
    if (!base) return;
    mossaDallUtente.current = true;
    setCamera(limita(c, base.scala, RIQUADRO_ITALIA));
  }, [base]);

  const ingrandisci = useCallback((fattore: number, punto?: { x: number; y: number }) => {
    const c = cameraRef.current;
    if (!c || !dim) return;
    mossaDallUtente.current = true;
    vaiA(ingrandisciAttorno(c, dim, fattore, punto ?? { x: dim.larghezza / 2, y: dim.altezza / 2 }));
  }, [dim, vaiA]);

  const tuttaLItalia = useCallback(() => {
    if (!base) return;
    mossaDallUtente.current = false;
    vaiA(base);
  }, [base, vaiA]);

  // ── i segni sullo schermo

  const mondo = useMemo(() => segni.map((a) => ({ a, p: proietta(a.lng, a.lat) })), [segni]);
  const perChiave = useMemo(() => new Map(segni.map((a) => [a.chiave, a])), [segni]);
  /** Il peso di un segno: gli account dell'azienda sulla sede principale, niente sulle altre (non si contano due volte). */
  const pesoDi = (a: SegnoDaDisegnare) => (a.principale ? a.account_totale : 0);
  /** Il raggio con cui il segno si disegna: lo stesso per raggruppare e per non scriverci sopra un nome. */
  const raggioDisegnato = (a: SegnoDaDisegnare) => (!a.principale
    ? RAGGIO_SEDE
    : a.logo ? Math.max(RAGGIO_MINIMO_CON_LOGO, raggioAlone(a.account_totale)) : raggioAlone(a.account_totale));

  const posizioniSchermo = useCallback((c: Camera) => {
    if (!dim) return [];
    return mondo.map(({ a, p }) => {
      const q = versoSchermo(p, c, dim);
      return { chiave: a.chiave, x: q.x, y: q.y, peso: pesoDi(a), raggio: raggioDisegnato(a) };
    });
  }, [mondo, dim]);

  const gruppiRaccolti: Gruppo[] = useMemo(() => (camera ? raggruppa(posizioniSchermo(camera)) : []), [camera, posizioniSchermo]);
  const puntoMondo = useMemo(() => new Map(mondo.map(({ a, p }) => [a.chiave, p])), [mondo]);

  /**
   * Vero se i segni si toccherebbero ancora all'ingrandimento massimo: avvicinarsi non li staccherà mai. Non basta che
   * siano «nello stesso punto»: tre aziende a 300 metri nella stessa zona industriale sono a pochi pixel anche da
   * vicinissimo, molto meno della larghezza dei loro segni (28/10/2026, Villacidro restava chiuso).
   */
  const inseparabili = useCallback((chiavi: readonly string[]) => {
    if (!base || chiavi.length < 2) return false;
    const scala = base.scala * INGRANDIMENTO_MASSIMO;
    const membri = chiavi.map((k) => ({ k, p: puntoMondo.get(k), a: perChiave.get(k) })).filter((m) => m.p && m.a);
    return raggruppa(membri.map((m) => ({ chiave: m.k, x: m.p!.x * scala, y: m.p!.y * scala, peso: pesoDi(m.a!), raggio: raggioDisegnato(m.a!) }))).length === 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, puntoMondo, perChiave]);

  // Il ventaglio: abbastanza vicino, un gruppo che non si staccherebbe mai si apre in cerchio attorno al punto vero.
  const gruppi: Array<Gruppo & { centro?: { x: number; y: number } }> = useMemo(() => {
    if (!camera || !base || camera.scala < base.scala * SOGLIA_VENTAGLIO) return gruppiRaccolti;
    return gruppiRaccolti.flatMap((g) => {
      if (g.chiavi.length < 2 || !inseparabili(g.chiavi)) return [g];
      const membri = g.chiavi
        .map((k) => perChiave.get(k))
        .filter((a): a is SegnoDaDisegnare => !!a)
        .sort((a, b) => pesoDi(b) - pesoDi(a) || a.nome.localeCompare(b.nome));
      const posti = aVentaglio({ x: g.x, y: g.y }, membri.map(raggioDisegnato));
      return membri.map((m, i) => ({ chiavi: [m.chiave], x: posti[i].x, y: posti[i].y, peso: pesoDi(m), raggio: raggioDisegnato(m), centro: { x: g.x, y: g.y } }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruppiRaccolti, camera, base, inseparabili, perChiave]);

  /** I segni di un gruppo, una volta per azienda (la sede principale se c'è, altrimenti la prima). */
  const aziendeDelGruppo = useCallback((chiavi: readonly string[]): SegnoDaDisegnare[] => {
    const perAzienda = new Map<string, SegnoDaDisegnare>();
    for (const k of chiavi) {
      const a = perChiave.get(k);
      if (!a) continue;
      const gia = perAzienda.get(a.azienda);
      if (!gia || (a.principale && !gia.principale)) perAzienda.set(a.azienda, a);
    }
    return [...perAzienda.values()];
  }, [perChiave]);

  /** Il testo accanto a un segno: il nome dell'azienda, la sede dell'azienda scelta, o i comuni di un gruppo. */
  const testoDelGruppo = useCallback((g: Gruppo): { testo: string; luogo: boolean } => {
    const aziende = aziendeDelGruppo(g.chiavi);
    if (aziende.length === 1) {
      const membri = g.chiavi.map((k) => perChiave.get(k)).filter((a): a is SegnoDaDisegnare => !!a);
      const principale = membri.find((m) => m.principale);
      if (principale) return { testo: nomeBreve(principale.nome), luogo: false };
      const sede = membri[0];
      return sede && sede.azienda === selezionata ? { testo: sede.nome_sede ?? sede.citta ?? '', luogo: true } : { testo: '', luogo: true };
    }
    const comuni = comuniDelGruppo(g.chiavi.map((k) => perChiave.get(k)).filter((a): a is SegnoDaDisegnare => !!a).map((a) => ({ citta: a.citta, peso: pesoDi(a) })));
    if (comuni.length === 0) return { testo: '', luogo: true };
    if (comuni.length === 1) return { testo: comuni[0], luogo: true };
    if (comuni.length === 2) return { testo: t('mappa.luoghi_due', { a: comuni[0], b: comuni[1] }), luogo: true };
    return { testo: t('mappa.luoghi_altri', { luogo: comuni[0], count: comuni.length - 1 }), luogo: true };
  }, [aziendeDelGruppo, perChiave, selezionata, t]);

  const delGruppoScelto = useCallback(
    (g: Gruppo) => !!selezionata && g.chiavi.some((k) => perChiave.get(k)?.azienda === selezionata),
    [perChiave, selezionata],
  );

  const etichette = useMemo(() => {
    if (!dim) return [];
    const candidati = gruppi.map((g) => {
      const chiave = chiaveDelGruppo(g.chiavi);
      const { testo, luogo } = testoDelGruppo(g);
      return {
        chiave,
        testo,
        luogo,
        x: g.x,
        y: g.y,
        raggio: aziendeDelGruppo(g.chiavi).length > 1 ? Math.max(g.raggio, 14) : g.raggio,
        larghezza: larghezzaTesto(testo),
        priorita: delGruppoScelto(g) ? Number.MAX_SAFE_INTEGER : g.peso,
      };
    }).filter((c) => c.testo);
    const ostacoli = gruppi.map((g) => ({ chiave: chiaveDelGruppo(g.chiavi), x: g.x, y: g.y, raggio: Math.max(g.raggio, 7) }));
    const posate = posaEtichette(candidati, ostacoli, { ...dim, margine: 6 }).filter((e) => e.y > alto - 4);
    const perCandidato = new Map(candidati.map((c) => [c.chiave, c]));
    return posate.map((e) => ({ ...e, testo: perCandidato.get(e.chiave)!.testo, luogo: perCandidato.get(e.chiave)!.luogo }));
  }, [gruppi, testoDelGruppo, aziendeDelGruppo, delGruppoScelto, dim, alto]);

  /** Porta la carta su un punto, abbastanza vicino da staccare quel segno dagli altri (se si può). */
  const portaSu = useCallback((chiaveSegno: string, scalaMinima = 0) => {
    const c = cameraRef.current;
    const segno = mondo.find(({ a }) => a.chiave === chiaveSegno);
    if (!c || !dim || !base || !segno) return;
    const staccato = (scala: number) => raggruppa(posizioniSchermo({ ...c, cx: segno.p.x, cy: segno.p.y, scala }))
      .some((g) => g.chiavi.length === 1 && g.chiavi[0] === chiaveSegno);
    const scalaMassima = base.scala * INGRANDIMENTO_MASSIMO;
    const sx = versoSchermo(segno.p, c, dim);
    const fuori = sx.x < sinistra || sx.x > dim.larghezza - destra || sx.y < alto || sx.y > dim.altezza - basso;
    let scala = Math.max(c.scala, Math.min(scalaMassima, scalaMinima));
    // Dentro un gruppo che si stacca avvicinandosi: si raddoppia finché il segno è solo. Un gruppo nello stesso punto
    // non si stacca mai: lì basta portarlo in vista, il pannello lo mostra già.
    if (staccato(scalaMassima)) while (scala < scalaMassima && !staccato(scala)) scala = Math.min(scalaMassima, scala * 2);
    // Nello stesso punto di altre: non si stacca mai, ma da vicino si apre a ventaglio.
    else scala = Math.max(scala, base.scala * SOGLIA_VENTAGLIO);
    if (scala === c.scala && !fuori) return;
    mossaDallUtente.current = true;
    vaiA({ cx: segno.p.x, cy: segno.p.y + ((basso - alto) / 2) / scala, scala });
  }, [mondo, dim, base, posizioniSchermo, vaiA, alto, basso, destra, sinistra]);

  // Una scelta fatta dal pannello porta la carta sulla sede principale dell'azienda.
  const ultimaScelta = useRef<string | null>(null);
  useEffect(() => {
    if (!selezionata || selezionata === ultimaScelta.current) {
      ultimaScelta.current = selezionata;
      return;
    }
    ultimaScelta.current = selezionata;
    const sue = segni.filter((a) => a.azienda === selezionata);
    const principale = sue.find((a) => a.principale) ?? sue[0];
    if (!principale) return;
    // Più sedi: si inquadrano tutte insieme, così le linee fra loro si vedono intere. Una sola: ci si avvicina quanto
    // basta a staccarla dalle altre aziende.
    if (sue.length > 1 && dim && base) {
      const punti = sue.map((a) => proietta(a.lng, a.lat));
      const riquadro = {
        minX: Math.min(...punti.map((p) => p.x)),
        maxX: Math.max(...punti.map((p) => p.x)),
        minY: Math.min(...punti.map((p) => p.y)),
        maxY: Math.max(...punti.map((p) => p.y)),
      };
      const margine = 70;
      mossaDallUtente.current = true;
      vaiA(inquadra(riquadro, dim, { alto: alto + margine, destra: destra + margine, basso: basso + margine, sinistra: sinistra + margine }, base.scala * INGRANDIMENTO_MASSIMO));
      return;
    }
    portaSu(principale.chiave);
  }, [selezionata, segni, portaSu, dim, base, vaiA, alto, destra, basso, sinistra]);

  // Una sede scelta dalla scheda: la carta ci va, più vicino.
  useEffect(() => {
    if (vaiAllaSede && base) portaSu(vaiAllaSede.chiave, base.scala * 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaiAllaSede?.n]);

  const apriGruppo = useCallback((g: Gruppo) => {
    const c = cameraRef.current;
    if (!c || !dim || !base) return;
    const punti = g.chiavi.map((k) => mondo.find(({ a }) => a.chiave === k)?.p).filter((p): p is { x: number; y: number } => !!p);
    const minX = Math.min(...punti.map((p) => p.x));
    const maxX = Math.max(...punti.map((p) => p.x));
    const minY = Math.min(...punti.map((p) => p.y));
    const maxY = Math.max(...punti.map((p) => p.y));
    const scalaMassima = base.scala * INGRANDIMENTO_MASSIMO;
    // Nello stesso punto anche da vicinissimo: ci si avvicina quanto basta ad aprirlo a ventaglio, e il pannello le elenca.
    if (inseparabili(g.chiavi)) {
      onGruppo(aziendeDelGruppo(g.chiavi).map((a) => a.azienda));
      mossaDallUtente.current = true;
      vaiA({ cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 + ((basso - alto) / 2) / Math.max(c.scala, base.scala * SOGLIA_VENTAGLIO), scala: Math.max(c.scala, base.scala * SOGLIA_VENTAGLIO) });
      return;
    }
    const riquadro = { minX, minY, maxX, maxY };
    const margine = 90;
    const destinazione = inquadra(riquadro, dim, { alto: alto + margine, destra: destra + margine, basso: basso + margine, sinistra: sinistra + margine }, scalaMassima);
    mossaDallUtente.current = true;
    vaiA({ ...destinazione, scala: Math.max(destinazione.scala, c.scala * 2) });
  }, [dim, base, mondo, onGruppo, aziendeDelGruppo, inseparabili, vaiA, alto, destra, basso, sinistra]);

  // ── i gesti

  const puntatori = useRef(new Map<number, { x: number; y: number }>());
  const gesto = useRef<
    | { tipo: 'trascina'; da: { x: number; y: number }; camera: Camera; mosso: boolean }
    | { tipo: 'pizzica'; distanza: number; centro: { x: number; y: number }; camera: Camera }
    | null
  >(null);
  const ignoraClic = useRef(false);

  const posizione = (e: React.PointerEvent | PointerEvent) => {
    const r = contenitore.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const iniziaGesto = () => {
    const c = cameraRef.current;
    if (!c) return;
    const punti = [...puntatori.current.values()];
    if (punti.length >= 2) {
      const [a, b] = punti;
      gesto.current = { tipo: 'pizzica', distanza: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), centro: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, camera: c };
      ignoraClic.current = true;
    } else if (punti.length === 1) {
      gesto.current = { tipo: 'trascina', da: punti[0], camera: c, mosso: false };
    } else {
      gesto.current = null;
    }
  };

  const giu = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    fermaVolo();
    puntatori.current.set(e.pointerId, posizione(e));
    ignoraClic.current = puntatori.current.size > 1;
    iniziaGesto();
  };

  const muovi = (e: React.PointerEvent) => {
    if (!puntatori.current.has(e.pointerId) || !dim) return;
    puntatori.current.set(e.pointerId, posizione(e));
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === 'trascina') {
      const p = puntatori.current.get(e.pointerId)!;
      const dx = p.x - g.da.x;
      const dy = p.y - g.da.y;
      if (!g.mosso && Math.hypot(dx, dy) < SOGLIA_TRASCINAMENTO) return;
      if (!g.mosso) {
        g.mosso = true;
        ignoraClic.current = true;
        contenitore.current?.setPointerCapture(e.pointerId);
      }
      imposta(sposta(g.camera, dx, dy));
    } else {
      const [a, b] = [...puntatori.current.values()];
      if (!a || !b) return;
      const centro = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const fattore = Math.hypot(a.x - b.x, a.y - b.y) / g.distanza;
      const spostata = sposta(g.camera, centro.x - g.centro.x, centro.y - g.centro.y);
      imposta(ingrandisciAttorno(spostata, dim, fattore, centro));
    }
  };

  const su = (e: React.PointerEvent) => {
    puntatori.current.delete(e.pointerId);
    iniziaGesto();
  };

  // La rotella va presa a mano: React registra `wheel` come passivo e non si può fermare lo scorrimento della pagina.
  useEffect(() => {
    const el = contenitore.current;
    if (!el) return;
    const rotella = (e: WheelEvent) => {
      const c = cameraRef.current;
      if (!c || !dim) return;
      e.preventDefault();
      fermaVolo();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const fattore = Math.exp(-delta * (e.ctrlKey ? 0.012 : 0.0024));
      const r = el.getBoundingClientRect();
      imposta(ingrandisciAttorno(c, dim, fattore, { x: e.clientX - r.left, y: e.clientY - r.top }));
    };
    el.addEventListener('wheel', rotella, { passive: false });
    return () => el.removeEventListener('wheel', rotella);
  }, [dim, imposta]);

  const tastiera = (e: React.KeyboardEvent) => {
    const c = cameraRef.current;
    if (!c) return;
    const passo = 80;
    switch (e.key) {
      case '+': case '=': ingrandisci(1.6); break;
      case '-': case '_': ingrandisci(1 / 1.6); break;
      case '0': tuttaLItalia(); break;
      case 'ArrowLeft': imposta(sposta(c, passo, 0)); break;
      case 'ArrowRight': imposta(sposta(c, -passo, 0)); break;
      case 'ArrowUp': imposta(sposta(c, 0, passo)); break;
      case 'ArrowDown': imposta(sposta(c, 0, -passo)); break;
      case 'Escape': onSeleziona(null); break;
      default: return;
    }
    e.preventDefault();
  };

  // ── le regioni delle sedi dell'azienda scelta si colorano col suo marchio

  const sediScelte = useMemo(() => (selezionata ? segni.filter((a) => a.azienda === selezionata) : []), [segni, selezionata]);
  const coloreScelto = sediScelte[0]?.colore ?? tokens.color.graphite;
  const regioniScelte = useMemo(() => {
    const insieme = new Set<string>();
    if (regioni) for (const a of sediScelte) {
      const r = regioneDelPunto(regioni, a.lng, a.lat);
      if (r) insieme.add(r);
    }
    return insieme;
  }, [sediScelte, regioni]);

  // Le linee fra le sedi dell'azienda scelta: dalla principale a ognuna delle altre.
  const linee = useMemo(() => {
    if (!camera || !dim || sediScelte.length < 2) return [];
    const principale = sediScelte.find((a) => a.principale) ?? sediScelte[0];
    const da = versoSchermo(proietta(principale.lng, principale.lat), camera, dim);
    return sediScelte.filter((a) => a !== principale).map((a) => ({ chiave: a.chiave, da, a: versoSchermo(proietta(a.lng, a.lat), camera, dim) }));
  }, [sediScelte, camera, dim]);

  // Gli archi di fornitura: sempre in vista ma leggeri; con un'azienda scelta restano solo i suoi, in evidenza.
  const archi = useMemo(() => {
    if (!camera || !dim || legami.length === 0) return [];
    const principali = new Map<string, SegnoDaDisegnare>();
    for (const s of segni) if (s.principale || !principali.has(s.azienda)) principali.set(s.azienda, s);
    return legami.flatMap((l) => {
      const f = principali.get(l.da);
      const c = principali.get(l.verso);
      if (!f || !c) return [];
      const daSchermo = versoSchermo(proietta(f.lng, f.lat), camera, dim);
      const aSchermo = versoSchermo(proietta(c.lng, c.lat), camera, dim);
      const coinvolto = !!selezionata && (l.da === selezionata || l.verso === selezionata);
      if (selezionata && !coinvolto) return [];
      return [{ chiave: `${l.da}>${l.verso}`, percorso: arcoFra(daSchermo, aSchermo), colore: f.colore ?? tokens.color.graphite, forte: coinvolto }];
    });
  }, [legami, segni, camera, dim, selezionata]);

  const ordinati = useMemo(() => [...gruppi].sort((a, b) => {
    const sa = delGruppoScelto(a) ? 1 : 0;
    const sb = delGruppoScelto(b) ? 1 : 0;
    return sa - sb || b.raggio - a.raggio;
  }), [gruppi, delGruppoScelto]);

  let indiceEntrata = 0;

  return (
    <Box
      ref={contenitore}
      tabIndex={0}
      role="application"
      aria-label={t('mappa.descrizione')}
      onPointerDown={giu}
      onPointerMove={muovi}
      onPointerUp={su}
      onPointerCancel={su}
      onDoubleClick={(e) => {
        const r = contenitore.current!.getBoundingClientRect();
        ingrandisci(2, { x: e.clientX - r.left, y: e.clientY - r.top });
      }}
      onClick={() => {
        if (ignoraClic.current) { ignoraClic.current = false; return; }
        onSeleziona(null);
      }}
      onKeyDown={tastiera}
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        bgcolor: COLORI_MAPPA.mare,
        touchAction: 'none',
        userSelect: 'none',
        cursor: gesto.current?.tipo === 'trascina' && gesto.current.mosso ? 'grabbing' : 'grab',
        outline: 'none',
        '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${tokens.color.graphite}` },
        '& .mappa-segno': { cursor: 'pointer', outline: 'none', transition: 'opacity 200ms ease' },
        '& .mappa-segno:focus-visible .mappa-segno__bersaglio': { stroke: tokens.color.graphite, strokeWidth: 1.5, strokeDasharray: '2 3' },
        '& .mappa-segno__entrata': { animation: 'mappa-entra 560ms cubic-bezier(.2,.8,.2,1) both', transformBox: 'fill-box', transformOrigin: 'center' },
        '& .mappa-segno__scelta': { animation: 'mappa-scelta 700ms ease-out', transformBox: 'fill-box', transformOrigin: 'center' },
        '@keyframes mappa-entra': { from: { transform: 'scale(0)', opacity: 0 }, to: { transform: 'scale(1)', opacity: 1 } },
        '@keyframes mappa-scelta': { from: { transform: 'scale(1.6)', opacity: 0 }, to: { transform: 'scale(1)', opacity: 1 } },
        '& .mappa-segno__onda': { animation: 'mappa-onda 2.4s cubic-bezier(.2,.6,.3,1) infinite', transformBox: 'fill-box', transformOrigin: 'center' },
        '@keyframes mappa-onda': { '0%': { transform: 'scale(1)', opacity: 0.7 }, '80%, 100%': { transform: 'scale(1.75)', opacity: 0 } },
        '@media (prefers-reduced-motion: reduce)': {
          '& .mappa-segno__entrata, & .mappa-segno__scelta, & .mappa-segno__onda': { animation: 'none' },
          '& .mappa-segno__onda': { display: 'none' },
        },
      }}
    >
      {camera && dim && (
        <>
          <svg width={dim.larghezza} height={dim.altezza} viewBox={viewBox(camera, dim)} style={{ position: 'absolute', inset: 0, display: 'block' }} aria-hidden>
            <path d={PERCORSO_RETICOLO} fill="none" stroke={COLORI_MAPPA.reticolo} strokeWidth={1} strokeDasharray="1 5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            {regioni && <Terra regioni={regioni} evidenziate={regioniScelte} colore={coloreScelto} ombra={2.4 / camera.scala} />}
          </svg>
          <svg width={dim.larghezza} height={dim.altezza} style={{ position: 'absolute', inset: 0, display: 'block', overflow: 'visible' }}>
            {gruppi.filter((g) => g.centro).map((g) => (
              <line
                key={`filo:${g.chiavi[0]}`}
                x1={g.centro!.x}
                y1={g.centro!.y}
                x2={g.x}
                y2={g.y}
                stroke={tokens.color.graphite}
                strokeWidth={1}
                opacity={0.45}
                style={{ pointerEvents: 'none' }}
              />
            ))}
            {[...new Map(gruppi.filter((g) => g.centro).map((g) => [`${g.centro!.x}|${g.centro!.y}`, g.centro!])).values()].map((c) => (
              <circle key={`centro:${c.x}|${c.y}`} cx={c.x} cy={c.y} r={3.5} fill={tokens.color.graphite} stroke={COLORI_MAPPA.terra} strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
            ))}
            {archi.map((a) => (
              <path
                key={`arco:${a.chiave}`}
                d={a.percorso}
                fill="none"
                stroke={a.colore}
                strokeWidth={a.forte ? 2 : 1.4}
                strokeLinecap="round"
                opacity={a.forte ? 0.85 : 0.3}
                style={{ pointerEvents: 'none', transition: 'opacity 200ms ease' }}
              />
            ))}
            {linee.map((l) => (
              <line
                key={`linea:${l.chiave}`}
                x1={l.da.x}
                y1={l.da.y}
                x2={l.a.x}
                y2={l.a.y}
                stroke={coloreScelto}
                strokeWidth={1.4}
                strokeDasharray="3 4"
                strokeLinecap="round"
                opacity={0.75}
                style={{ pointerEvents: 'none' }}
              />
            ))}
            {ordinati.map((g) => {
              const aziende = aziendeDelGruppo(g.chiavi);
              if (aziende.length > 1) {
                return (
                  <SegnoGruppo
                    key={chiaveDelGruppo(g.chiavi)}
                    x={g.x}
                    y={g.y}
                    raggio={g.raggio}
                    quante={aziende.length}
                    account={sommaConti(aziende.map((m) => m.account))}
                    contieneLaScelta={delGruppoScelto(g)}
                    spento={aziende.every((m) => m.spenta)}
                    alLavoro={aziende.some((m) => m.persone_adesso > 0)}
                    onScegli={() => apriGruppo(g)}
                  />
                );
              }
              const membri = g.chiavi.map((k) => perChiave.get(k)).filter((m): m is SegnoDaDisegnare => !!m);
              const a = membri.find((m) => m.principale) ?? membri[0];
              if (!a) return null;
              const scelta = a.azienda === selezionata;
              const scegli = () => onSeleziona(scelta ? null : a.azienda);
              if (!a.principale) {
                return (
                  <SegnoSede
                    key={chiaveDelGruppo(g.chiavi)}
                    x={g.x}
                    y={g.y}
                    livello={a.livello}
                    spenta={a.spenta}
                    selezionata={scelta}
                    attenuata={!!selezionata && !scelta}
                    etichetta={t('mappa.sede', { azienda: a.nome, sede: a.nome_sede ?? a.citta ?? '' })}
                    onScegli={scegli}
                  />
                );
              }
              return (
                <SegnoAzienda
                  key={chiaveDelGruppo(g.chiavi)}
                  x={g.x}
                  y={g.y}
                  raggio={raggioDisegnato(a)}
                  livello={a.livello}
                  account={a.account}
                  accountTotale={a.account_totale}
                  personeAttive={a.persone_attive}
                  spenta={a.spenta}
                  selezionata={scelta}
                  attenuata={!!selezionata && !scelta}
                  alLavoro={a.persone_adesso > 0}
                  logo={a.logo}
                  etichetta={a.persone_adesso > 0
                    ? t('mappa.segno_adesso', { nome: a.nome, account: a.account_totale, adesso: a.persone_adesso })
                    : t('mappa.segno', { nome: a.nome, account: a.account_totale, attive: a.persone_attive })}
                  ritardo={entrata ? Math.min(900, 60 * indiceEntrata++) : null}
                  onScegli={scegli}
                />
              );
            })}
            {etichette.map((e) => (
              <NomeSullaMappa
                key={`nome:${e.chiave}`}
                x={e.x}
                y={e.y}
                ancora={e.ancora}
                testo={e.testo}
                tono={e.luogo ? 'luogo' : 'azienda'}
                forte={!e.luogo && e.chiave.split('|')[0] === selezionata}
              />
            ))}
          </svg>
        </>
      )}

      <Paper
        elevation={0}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          right: 16,
          bottom: spazioControlliBasso + 16,
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${tokens.color.line}`,
          borderRadius: `${tokens.radius.md}px`,
          overflow: 'hidden',
          bgcolor: tokens.color.paper,
          '& .MuiIconButton-root': { width: 44, height: 44, borderRadius: 0, color: tokens.color.graphite },
          '& .MuiIconButton-root + .MuiIconButton-root': { borderTop: `1px solid ${tokens.color.lineSoft}` },
        }}
      >
        <Tooltip title={t('mappa.avvicina')} placement="left">
          <IconButton aria-label={t('mappa.avvicina')} onClick={() => ingrandisci(1.8)}><Add fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={t('mappa.allontana')} placement="left">
          <IconButton aria-label={t('mappa.allontana')} onClick={() => ingrandisci(1 / 1.8)}><Remove fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={t('mappa.tutta_italia')} placement="left">
          <IconButton aria-label={t('mappa.tutta_italia')} onClick={tuttaLItalia}><ZoomOutMap fontSize="small" /></IconButton>
        </Tooltip>
      </Paper>
    </Box>
  );
};
